import { useMutation, useQueryClient } from "@tanstack/react-query";
import { reportMutations } from "../../../../mutations";
import { reportQueries } from "../../../../queries";
import type { AnalysisStepRow } from "@/types/supabase.types";

interface UpdateReportStepParams {
  stepId: string;
  value: string;
  reportId: string;
}

export function useUpdateReportStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ stepId, value }: UpdateReportStepParams) => {
      const { error } = await reportMutations.analysisStep.update(
        stepId,
        value,
      );
      if (error) throw error;
      return { stepId, value };
    },
    onMutate: async ({ stepId, value, reportId }) => {
      const stepsQueryKey = reportQueries.steps(reportId).queryKey;
      await queryClient.cancelQueries({ queryKey: stepsQueryKey });

      const previous =
        queryClient.getQueryData<AnalysisStepRow[]>(stepsQueryKey);

      // Optimistically update
      if (previous) {
        queryClient.setQueryData<AnalysisStepRow[]>(stepsQueryKey, (old) =>
          (old ?? []).map((s) =>
            s.analysis_step_id === stepId ? { ...s, value } : s,
          ),
        );
      }

      return { previous, stepsQueryKey };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.stepsQueryKey, context.previous);
      }
    },
    onSettled: async (_data, _error, { reportId }) => {
      await Promise.all([
        queryClient.invalidateQueries(reportQueries.stepsKey()),
        queryClient.invalidateQueries(reportQueries.detail(reportId)),
      ]);
    },
  });
}
